import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ShieldCheck, 
  Loader2, 
  Check, 
  Plus,
  Trash2,
  Upload,
  Image,
  ArrowLeft,
  ArrowRight,
  FileText,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { API } from "@/App";

const NBR_LOGO = "https://nationalbusinessregister.co.uk/wp-content/uploads/2024/11/logo.png";

// UK Nice Classification Categories
const NICE_CLASSES = [
  { value: "1", label: "Class 1 - Chemicals" },
  { value: "2", label: "Class 2 - Paints" },
  { value: "3", label: "Class 3 - Cosmetics & Cleaning" },
  { value: "4", label: "Class 4 - Lubricants & Fuels" },
  { value: "5", label: "Class 5 - Pharmaceuticals" },
  { value: "6", label: "Class 6 - Metal Goods" },
  { value: "7", label: "Class 7 - Machinery" },
  { value: "8", label: "Class 8 - Hand Tools" },
  { value: "9", label: "Class 9 - Electrical & Scientific" },
  { value: "10", label: "Class 10 - Medical Apparatus" },
  { value: "11", label: "Class 11 - Environmental Control" },
  { value: "12", label: "Class 12 - Vehicles" },
  { value: "13", label: "Class 13 - Firearms" },
  { value: "14", label: "Class 14 - Jewellery" },
  { value: "15", label: "Class 15 - Musical Instruments" },
  { value: "16", label: "Class 16 - Paper Goods & Printed Matter" },
  { value: "17", label: "Class 17 - Rubber Goods" },
  { value: "18", label: "Class 18 - Leather Goods" },
  { value: "19", label: "Class 19 - Building Materials" },
  { value: "20", label: "Class 20 - Furniture" },
  { value: "21", label: "Class 21 - Housewares" },
  { value: "22", label: "Class 22 - Cordage & Fibres" },
  { value: "23", label: "Class 23 - Yarns & Threads" },
  { value: "24", label: "Class 24 - Fabrics" },
  { value: "25", label: "Class 25 - Clothing" },
  { value: "26", label: "Class 26 - Fancy Goods" },
  { value: "27", label: "Class 27 - Floor Coverings" },
  { value: "28", label: "Class 28 - Toys & Sporting Goods" },
  { value: "29", label: "Class 29 - Meats & Processed Foods" },
  { value: "30", label: "Class 30 - Staple Foods" },
  { value: "31", label: "Class 31 - Natural Agricultural Products" },
  { value: "32", label: "Class 32 - Light Beverages" },
  { value: "33", label: "Class 33 - Wine & Spirits" },
  { value: "34", label: "Class 34 - Smokers' Articles" },
  { value: "35", label: "Class 35 - Advertising & Business" },
  { value: "36", label: "Class 36 - Insurance & Financial" },
  { value: "37", label: "Class 37 - Building & Repair" },
  { value: "38", label: "Class 38 - Telecommunications" },
  { value: "39", label: "Class 39 - Transportation & Storage" },
  { value: "40", label: "Class 40 - Treatment of Materials" },
  { value: "41", label: "Class 41 - Education & Entertainment" },
  { value: "42", label: "Class 42 - Computer & Scientific" },
  { value: "43", label: "Class 43 - Hotels & Restaurants" },
  { value: "44", label: "Class 44 - Medical & Beauty" },
  { value: "45", label: "Class 45 - Legal & Security" }
];

const TRADEMARK_STATUSES = [
  { value: "registered", label: "Registered" },
  { value: "examination", label: "Under Examination" },
  { value: "published", label: "Published" },
  { value: "opposed", label: "Opposed" },
  { value: "pending", label: "Pending" }
];

const emptyTrademark = {
  name: "",
  description: "",
  owner: "",
  owner_address: "",
  contact_email: "",
  contact_phone: "",
  categories: [],
  registration_number: "",
  registration_date: "",
  trademark_status: "registered",
  logo: null,
  logoPreview: null
};

export const TrademarkIntakeForm = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentTrademarkIndex, setCurrentTrademarkIndex] = useState(0);
  const [trademarks, setTrademarks] = useState([{ ...emptyTrademark }]);
  const [errors, setErrors] = useState({});

  const currentTrademark = trademarks[currentTrademarkIndex];

  const updateCurrentTrademark = (field, value) => {
    setTrademarks(prev => {
      const updated = [...prev];
      updated[currentTrademarkIndex] = { ...updated[currentTrademarkIndex], [field]: value };
      return updated;
    });
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Logo file must be less than 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        updateCurrentTrademark('logo', file);
        updateCurrentTrademark('logoPreview', reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    updateCurrentTrademark('logo', null);
    updateCurrentTrademark('logoPreview', null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const toggleCategory = (categoryValue) => {
    const current = currentTrademark.categories || [];
    if (current.includes(categoryValue)) {
      updateCurrentTrademark('categories', current.filter(c => c !== categoryValue));
    } else {
      updateCurrentTrademark('categories', [...current, categoryValue]);
    }
  };

  const addNewTrademark = () => {
    if (!validateCurrentTrademark()) {
      toast.error('Please complete all required fields before adding another trade mark');
      return;
    }
    setTrademarks(prev => [...prev, { ...emptyTrademark }]);
    setCurrentTrademarkIndex(trademarks.length);
    setErrors({});
  };

  const removeTrademark = (index) => {
    if (trademarks.length === 1) {
      toast.error('You must have at least one trade mark');
      return;
    }
    setTrademarks(prev => prev.filter((_, i) => i !== index));
    if (currentTrademarkIndex >= index && currentTrademarkIndex > 0) {
      setCurrentTrademarkIndex(prev => prev - 1);
    }
  };

  const validateCurrentTrademark = () => {
    const newErrors = {};
    const tm = currentTrademark;

    if (!tm.name?.trim()) newErrors.name = "Trade mark name is required";
    if (!tm.owner?.trim()) newErrors.owner = "Owner name is required";
    if (!tm.registration_number?.trim()) newErrors.registration_number = "Registration number is required";
    if (!tm.contact_email?.trim()) {
      newErrors.contact_email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(tm.contact_email)) {
      newErrors.contact_email = "Invalid email format";
    }
    if (!tm.categories || tm.categories.length === 0) {
      newErrors.categories = "At least one category is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateAllTrademarks = () => {
    for (let i = 0; i < trademarks.length; i++) {
      const tm = trademarks[i];
      if (!tm.name?.trim() || !tm.owner?.trim() || !tm.contact_email?.trim() || 
          !tm.categories || tm.categories.length === 0) {
        setCurrentTrademarkIndex(i);
        validateCurrentTrademark();
        return false;
      }
    }
    return true;
  };

  const handleProceedToPayment = async () => {
    if (!validateAllTrademarks()) {
      toast.error('Please complete all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      // Prepare trademarks data
      const trademarksData = trademarks.map(tm => ({
        name: tm.name,
        description: tm.description,
        owner: tm.owner,
        owner_address: tm.owner_address,
        contact_email: tm.contact_email,
        contact_phone: tm.contact_phone,
        categories: tm.categories,
        registration_number: tm.registration_number,
        registration_date: tm.registration_date,
        trademark_status: tm.trademark_status,
        logo_base64: tm.logoPreview
      }));

      // Save submission to database
      const contactEmail = trademarks[0]?.contact_email || '';
      const response = await axios.post(`${API}/submissions/create`, {
        trademarks: trademarksData,
        contact_email: contactEmail
      });
      
      const submissionId = response.data.submission_id;

      // Store in session storage for the payment page
      sessionStorage.setItem('pending_trademarks', JSON.stringify(trademarksData));
      sessionStorage.setItem('submission_id', submissionId);

      // Navigate to signup with trademarks
      navigate('/signup?has_trademarks=true');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <button 
            onClick={() => navigate('/landing')}
            className="inline-flex items-center gap-2 text-slate-500 hover:text-teal-600 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </button>
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src={NBR_LOGO} alt="NBR" className="h-10 w-auto" />
            <div>
              <span className="font-heading font-black text-lg text-slate-900">NBR</span>
              <span className="text-xs text-teal-600 tracking-widest uppercase ml-1">Trade Mark Monitoring</span>
            </div>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
            Register Your Trade Marks
          </h1>
          <p className="text-slate-500 mb-3">
            Tell us about the trade marks you want us to monitor
          </p>
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 text-sm text-teal-700">
            <strong>You can register multiple trade marks using this form.</strong> Simply add each trade mark one by one using the "Add Another Trade Mark" button below. Volume discounts apply automatically based on the number of marks you register.
          </div>
        </div>

        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="flex items-center gap-2 text-teal-600">
            <div className="w-8 h-8 rounded-full bg-teal-600 text-white flex items-center justify-center text-sm font-bold">
              1
            </div>
            <span className="text-sm font-medium">Trade Mark Details</span>
          </div>
          <div className="w-12 h-0.5 bg-slate-300" />
          <div className="flex items-center gap-2 text-slate-400">
            <div className="w-8 h-8 rounded-full bg-slate-200 text-slate-500 flex items-center justify-center text-sm font-bold">
              2
            </div>
            <span className="text-sm font-medium">Payment</span>
          </div>
          <div className="w-12 h-0.5 bg-slate-300" />
          <div className="flex items-center gap-2 text-slate-400">
            <div className="w-8 h-8 rounded-full bg-slate-200 text-slate-500 flex items-center justify-center text-sm font-bold">
              3
            </div>
            <span className="text-sm font-medium">Complete</span>
          </div>
        </div>

        {/* Trade mark tabs */}
        {trademarks.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-6">
            {trademarks.map((tm, index) => (
              <button
                key={index}
                onClick={() => setCurrentTrademarkIndex(index)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                  index === currentTrademarkIndex
                    ? 'bg-teal-600 text-white'
                    : 'bg-white text-slate-600 border border-slate-200 hover:border-teal-300'
                }`}
              >
                <FileText className="w-4 h-4" />
                {tm.name || `Trade Mark ${index + 1}`}
                {trademarks.length > 1 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeTrademark(index);
                    }}
                    className="ml-1 hover:text-red-300"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Form */}
        <motion.div
          key={currentTrademarkIndex}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-xl shadow-xl border border-slate-200 p-6 sm:p-8"
        >
          <div className="grid md:grid-cols-2 gap-6">
            {/* Trade Mark Name */}
            <div className="md:col-span-2">
              <Label htmlFor="name" className="text-slate-700">
                Trade Mark Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                value={currentTrademark.name}
                onChange={(e) => updateCurrentTrademark('name', e.target.value)}
                placeholder="Enter your trade mark name"
                className={errors.name ? "border-red-500" : ""}
                data-testid="tm-name-input"
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <Label htmlFor="description" className="text-slate-700">
                Description
              </Label>
              <Textarea
                id="description"
                value={currentTrademark.description}
                onChange={(e) => updateCurrentTrademark('description', e.target.value)}
                placeholder="Describe your trade mark and the goods/services it covers"
                rows={3}
                data-testid="tm-description-input"
              />
            </div>

            {/* Owner */}
            <div>
              <Label htmlFor="owner" className="text-slate-700">
                Owner / Company Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="owner"
                value={currentTrademark.owner}
                onChange={(e) => updateCurrentTrademark('owner', e.target.value)}
                placeholder="Legal owner of the trade mark"
                className={errors.owner ? "border-red-500" : ""}
                data-testid="tm-owner-input"
              />
              {errors.owner && <p className="text-red-500 text-xs mt-1">{errors.owner}</p>}
            </div>

            {/* Owner Address */}
            <div>
              <Label htmlFor="owner_address" className="text-slate-700">
                Owner Address
              </Label>
              <Textarea
                id="owner_address"
                value={currentTrademark.owner_address}
                onChange={(e) => updateCurrentTrademark('owner_address', e.target.value)}
                placeholder="Current address of the trade mark owner (in case it differs from the Registry)"
                rows={3}
                data-testid="tm-owner-address-input"
              />
            </div>

            {/* Status */}
            <div>
              <Label htmlFor="status" className="text-slate-700">
                Current Status <span className="text-red-500">*</span>
              </Label>
              <Select
                value={currentTrademark.trademark_status}
                onValueChange={(value) => updateCurrentTrademark('trademark_status', value)}
              >
                <SelectTrigger data-testid="tm-status-select">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {TRADEMARK_STATUSES.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Contact Email */}
            <div>
              <Label htmlFor="contact_email" className="text-slate-700">
                Contact Email <span className="text-red-500">*</span>
              </Label>
              <Input
                id="contact_email"
                type="email"
                value={currentTrademark.contact_email}
                onChange={(e) => updateCurrentTrademark('contact_email', e.target.value)}
                placeholder="email@company.com"
                className={errors.contact_email ? "border-red-500" : ""}
                data-testid="tm-email-input"
              />
              {errors.contact_email && <p className="text-red-500 text-xs mt-1">{errors.contact_email}</p>}
            </div>

            {/* Contact Phone */}
            <div>
              <Label htmlFor="contact_phone" className="text-slate-700">
                Contact Phone
              </Label>
              <Input
                id="contact_phone"
                value={currentTrademark.contact_phone}
                onChange={(e) => updateCurrentTrademark('contact_phone', e.target.value)}
                placeholder="07XXX XXXXXX"
                data-testid="tm-phone-input"
              />
            </div>

            {/* Registration Number */}
            <div>
              <Label htmlFor="registration_number" className="text-slate-700">
                Registration Number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="registration_number"
                value={currentTrademark.registration_number}
                onChange={(e) => updateCurrentTrademark('registration_number', e.target.value)}
                placeholder="UK00XXXXXXX"
                className={errors.registration_number ? "border-red-500" : ""}
                data-testid="tm-reg-number-input"
              />
              {errors.registration_number && <p className="text-red-500 text-xs mt-1">{errors.registration_number}</p>}
            </div>

            {/* Registration Date */}
            <div>
              <Label htmlFor="registration_date" className="text-slate-700">
                Registration Date
              </Label>
              <Input
                id="registration_date"
                type="date"
                value={currentTrademark.registration_date}
                onChange={(e) => updateCurrentTrademark('registration_date', e.target.value)}
                data-testid="tm-reg-date-input"
              />
            </div>

            {/* Logo Upload */}
            <div className="md:col-span-2">
              <Label className="text-slate-700">Trade Mark Logo</Label>
              <div className="mt-2">
                {currentTrademark.logoPreview ? (
                  <div className="flex items-center gap-4">
                    <div className="w-24 h-24 rounded-lg border-2 border-slate-200 overflow-hidden bg-slate-50 flex items-center justify-center">
                      <img 
                        src={currentTrademark.logoPreview} 
                        alt="Logo preview" 
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                    <div>
                      <p className="text-sm text-slate-600 mb-2">Logo uploaded</p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={removeLogo}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Remove
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-teal-400 hover:bg-teal-50/50 transition-colors"
                  >
                    <Image className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                    <p className="text-sm text-slate-600 mb-1">
                      Click to upload your trade mark logo
                    </p>
                    <p className="text-xs text-slate-400">
                      PNG, JPG up to 5MB
                    </p>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  data-testid="tm-logo-input"
                />
              </div>
            </div>

            {/* Categories */}
            <div className="md:col-span-2">
              <Label className="text-slate-700">
                Nice Classification Categories <span className="text-red-500">*</span>
              </Label>
              <p className="text-xs text-slate-500 mb-3">
                Select all classes that apply to your trade mark
              </p>
              {errors.categories && <p className="text-red-500 text-xs mb-2">{errors.categories}</p>}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-60 overflow-y-auto border border-slate-200 rounded-lg p-3">
                {NICE_CLASSES.map((cat) => (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => toggleCategory(cat.value)}
                    className={`text-left px-3 py-2 rounded-lg text-xs transition-colors ${
                      currentTrademark.categories?.includes(cat.value)
                        ? 'bg-teal-100 text-teal-700 border border-teal-300'
                        : 'bg-slate-50 text-slate-600 border border-slate-200 hover:border-teal-300'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
              {currentTrademark.categories?.length > 0 && (
                <p className="text-xs text-teal-600 mt-2">
                  {currentTrademark.categories.length} class(es) selected
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="mt-8 pt-6 border-t border-slate-200">
            <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
              <Button
                type="button"
                variant="outline"
                onClick={addNewTrademark}
                className="w-full sm:w-auto border-teal-600 text-teal-600 hover:bg-teal-50"
                data-testid="add-more-tm-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add More Trade Marks
              </Button>

              <div className="flex gap-3 w-full sm:w-auto">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/landing')}
                  className="flex-1 sm:flex-none"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleProceedToPayment}
                  disabled={isSubmitting}
                  className="flex-1 sm:flex-none bg-teal-600 hover:bg-teal-700 text-white px-8"
                  data-testid="proceed-payment-btn"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <ArrowRight className="w-4 h-4 mr-2" />
                  )}
                  Proceed to Payment
                </Button>
              </div>
            </div>

            <p className="text-center text-xs text-slate-500 mt-4">
              {trademarks.length} trade mark{trademarks.length > 1 ? 's' : ''} ready for monitoring
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TrademarkIntakeForm;
